import { ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    Bot,
    Database,
    FileText,
    Gauge,
    Home,
    LogOut,
    Menu,
    Server,
    Settings,
    Terminal,
    Users,
    X,
} from "lucide-react";
import { ThemeToggle } from "@/shared/components/theme/theme-toggle";
import { cn } from "@/shared/lib/utils";

interface User {
  id?: string
  username?: string
  email?: string
  role?: string
  discord_id?: string
}

interface AdminLayoutProps {
  user?: User | null
  onLogout?: () => void
  children?: ReactNode
}

interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  section?: string
}

const navItems: NavItem[] = [
    { to: "/admin/dashboard", label: "Home", icon: Home, section: "overview" },
    {
        to: "/admin/servers",
        label: "Server",
        icon: Server,
        section: "infrastructure",
    },
    { to: "/admin/bot", label: "Bot", icon: Bot, section: "infrastructure" },
    { to: "/admin/users", label: "Utenti", icon: Users, section: "management" },
    {
        to: "/admin/monitoring",
        label: "Monitor",
        icon: Gauge,
        section: "management",
    },
    {
        to: "/admin/database",
        label: "Database",
        icon: Database,
        section: "tools",
    },
    {
        to: "/admin/terminal",
        label: "Terminale",
        icon: Terminal,
        section: "tools",
    },
    { to: "/admin/api", label: "API", icon: FileText, section: "tools" },
    {
        to: "/admin/settings",
        label: "Settings",
        icon: Settings,
        section: "settings",
    },
];

export function AdminLayout({
  user,
  onLogout,
  children
}: AdminLayoutProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);

  const mainNavItems = useMemo(() => navItems.slice(0, 3), []);
  const extraNavItems = useMemo(() => navItems.slice(3), []);

  const isRouteActive = (path: string) =>
      location.pathname === path || location.pathname.startsWith(path + "/");

  const currentSection = useMemo(() => {
      if (location.pathname.startsWith("/admin/zomboid")) {
          return "infrastructure";
      }
      const activeItem = navItems.find((item) => isRouteActive(item.to));
      return activeItem?.section || "overview";
  }, [location.pathname]);

  const isExtraActive = useMemo(
      () => extraNavItems.some((item) => isRouteActive(item.to)),
      [extraNavItems, location.pathname],
  );

  useEffect(() => {
      setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const renderNavItem = (
      item: NavItem,
      variant: "desktop" | "mobile-sheet" | "mobile-bottom",
  ) => {
      const Icon = item.icon;
      const isActive = isRouteActive(item.to);

      if (variant === "mobile-bottom") {
          return (
              <NavLink
                  key={item.to}
                  to={item.to}
                  className={cn(
                      "flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-2 py-2 text-[11px] font-medium transition-colors",
                      isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground",
                  )}
              >
                  <Icon className="h-5 w-5" />
                  <span className="truncate">{item.label}</span>
              </NavLink>
          );
      }

      if (variant === "desktop") {
          return (
              <NavLink
                  key={item.to}
                  to={item.to}
                  className={cn(
                      "group flex items-center gap-3 rounded-xl border px-3 py-2 text-sm transition-colors",
                      isActive
                          ? "border-primary/30 bg-primary text-primary-foreground shadow-sm"
                          : "border-border/60 bg-background/20 text-muted-foreground hover:border-border hover:bg-muted/70 hover:text-foreground",
                  )}
              >
                  <Icon className="h-4 w-4 shrink-0" />
                  {isDesktopSidebarOpen && <span>{item.label}</span>}
              </NavLink>
          );
      }

      return (
          <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                  "flex items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors",
                  isActive
                      ? "border-primary/30 bg-primary text-primary-foreground"
                      : "border-border/60 bg-background/20 text-muted-foreground hover:border-border hover:bg-muted/70 hover:text-foreground",
              )}
          >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
          </NavLink>
      );
  };

  return (
      <div className="admin-enterprise min-h-screen bg-background">
          <div className="pointer-events-none fixed inset-0 -z-10 bg-gradient-to-b from-primary/10 via-background to-background" />
          <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.16),transparent_45%),radial-gradient(circle_at_bottom_left,hsl(var(--accent)/0.12),transparent_42%)]" />

          <div className="flex min-h-screen">
              <aside
                  className={cn(
                      "sticky top-0 hidden h-screen border-r border-border/80 bg-card/85 backdrop-blur-xl lg:flex lg:flex-col lg:transition-all",
                      isDesktopSidebarOpen ? "lg:w-64" : "lg:w-20",
                  )}
              >
                  <div className="flex h-16 items-center gap-3 border-b border-border/70 px-4">
                      <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border border-border/60 bg-background">
                          <img
                              src="/logo.png"
                              alt="Un Mondo Marrano"
                              className="h-8 w-8 object-contain"
                              loading="eager"
                          />
                      </div>
                      {isDesktopSidebarOpen && (
                          <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">
                                  UMMR Admin
                              </p>
                              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  {currentSection}
                              </p>
                          </div>
                      )}
                  </div>

                  <nav className="flex-1 space-y-2 overflow-y-auto p-3">
                      {navItems.map((item) => renderNavItem(item, "desktop"))}
                  </nav>

                  <div className="space-y-3 border-t border-border/70 p-3">
                      <div
                          className={cn(
                              "flex items-center gap-3 rounded-xl border border-border/70 bg-background/70 px-3 py-2",
                              !isDesktopSidebarOpen && "justify-center px-2",
                          )}
                      >
                          <ThemeToggle />
                          {isDesktopSidebarOpen && (
                              <span className="text-xs text-muted-foreground">
                                  Tema
                              </span>
                          )}
                      </div>
                      {user && (
                          <div
                              className={cn(
                                  "rounded-xl border border-border/70 bg-background/70 p-3",
                                  !isDesktopSidebarOpen && "p-2",
                              )}
                          >
                              {isDesktopSidebarOpen && (
                                  <p className="truncate text-sm font-medium">
                                      {user.username || user.email}
                                  </p>
                              )}
                              {onLogout && (
                                  <button
                                      onClick={onLogout}
                                      className={cn(
                                          "mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-border/80 px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                                          !isDesktopSidebarOpen && "mt-0 px-2",
                                      )}
                                      title="Logout"
                                  >
                                      <LogOut className="h-4 w-4" />
                                      {isDesktopSidebarOpen && <span>Logout</span>}
                                  </button>
                              )}
                          </div>
                      )}
                      <button
                          onClick={() =>
                              setIsDesktopSidebarOpen((current) => !current)
                          }
                          className="flex w-full items-center justify-center rounded-lg border border-border/80 px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                          {isDesktopSidebarOpen ? "Compatta" : "Espandi"}
                      </button>
                  </div>
              </aside>

              <div className="flex min-h-screen w-full flex-col">
                  <header className="sticky top-0 z-40 border-b border-border/80 bg-card/80 backdrop-blur-lg lg:hidden">
                      <div className="flex h-14 items-center justify-between px-4">
                          <div className="flex items-center gap-2">
                              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg border border-border/60 bg-background">
                                  <img
                                      src="/logo.png"
                                      alt="Un Mondo Marrano"
                                      className="h-7 w-7 object-contain"
                                      loading="eager"
                                  />
                              </div>
                              <div>
                                  <p className="text-sm font-semibold">
                                      UMMR Admin
                                  </p>
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                      {currentSection}
                                  </p>
                              </div>
                          </div>
                          <ThemeToggle />
                      </div>
                  </header>

                  <main className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-4 pb-28 sm:px-6 sm:py-6 lg:px-8 lg:pb-8">
                      {children}
                  </main>
              </div>
          </div>

          {isMobileMenuOpen && (
              <button
                  type="button"
                  aria-label="Chiudi menu"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm lg:hidden"
              />
          )}

          <div
              className={cn(
                  "fixed inset-x-0 bottom-0 z-50 border-t border-border/80 bg-card/95 backdrop-blur-lg transition-transform lg:hidden",
              )}
          >
              <div className="px-3 pb-2 pt-2">
                  <div className="flex items-center gap-1 rounded-2xl bg-muted/50 p-1">
                      {mainNavItems.map((item) =>
                          renderNavItem(item, "mobile-bottom"),
                      )}
                      <button
                          type="button"
                          onClick={() =>
                              setIsMobileMenuOpen((current) => !current)
                          }
                          className={cn(
                              "flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-2 py-2 text-[11px] font-medium transition-colors",
                              isExtraActive || isMobileMenuOpen
                                  ? "bg-primary/10 text-primary"
                                  : "text-muted-foreground",
                          )}
                          aria-expanded={isMobileMenuOpen}
                          aria-controls="admin-mobile-menu"
                      >
                          {isMobileMenuOpen ? (
                              <X className="h-5 w-5" />
                          ) : (
                              <Menu className="h-5 w-5" />
                          )}
                          <span className="truncate">Altro</span>
                      </button>
                  </div>
              </div>

              <div
                  id="admin-mobile-menu"
                  className={cn(
                      "overflow-hidden border-t border-border/70 px-3 transition-all",
                      isMobileMenuOpen
                          ? "max-h-80 pb-4 pt-3"
                          : "max-h-0 border-transparent py-0",
                  )}
              >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {extraNavItems.map((item) =>
                          renderNavItem(item, "mobile-sheet"),
                      )}
                  </div>
                  {user && onLogout && (
                      <button
                          onClick={onLogout}
                          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-border/80 px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                          <LogOut className="h-4 w-4" />
                          <span>Logout</span>
                      </button>
                  )}
              </div>
          </div>
      </div>
  );
}
